// frontend/src/pages/OrdersApiKeys.js
// Admin page for managing external Orders API keys (/api/v1/ feed consumed
// by the Willowbrook Management Hub). Keys are shown ONCE at creation.

import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Tooltip,
  IconButton,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add,
  Block,
  ContentCopy,
  Delete,
  Key,
  PictureAsPdf,
  Refresh,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { ordersApiKeysAPI } from '../services/api';
import usePageTitle from '../hooks/usePageTitle';

const STATUS_CHIP = {
  active: { label: 'Active', color: 'success' },
  revoked: { label: 'Revoked', color: 'error' },
  expired: { label: 'Expired', color: 'warning' },
};

const formatDateTime = (iso) => (iso ? new Date(iso).toLocaleString() : '—');
const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : 'Never');

const statusColor = (code) => {
  if (code === 200) return 'success';
  if (code === 429) return 'warning';
  if (code >= 500) return 'error';
  return 'error'; // 4xx
};

function StatCard({ label, value, hint }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="h5">{value}</Typography>
        {hint && (
          <Typography variant="caption" color="text.secondary">{hint}</Typography>
        )}
      </CardContent>
    </Card>
  );
}

function OrdersApiKeys() {
  usePageTitle('Orders API');

  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState(null);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newExpiry, setNewExpiry] = useState('');
  const [creating, setCreating] = useState(false);

  // One-time key reveal dialog
  const [createdKey, setCreatedKey] = useState(null);

  // Revoke / delete confirmation
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadKeys = useCallback(async () => {
    try {
      const response = await ordersApiKeysAPI.list();
      setKeys(response.data.keys);
    } catch (error) {
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUsage = useCallback(async () => {
    try {
      const response = await ordersApiKeysAPI.usage();
      setUsage(response.data);
    } catch (error) {
      toast.error('Failed to load API usage');
    }
  }, []);

  useEffect(() => {
    loadKeys();
    loadUsage();
  }, [loadKeys, loadUsage]);

  const handleCreate = async () => {
    if (!newName.trim()) {
      toast.error('Please enter a name for the key');
      return;
    }
    setCreating(true);
    try {
      const response = await ordersApiKeysAPI.create({
        name: newName.trim(),
        expires_at: newExpiry || null,
      });
      setCreateOpen(false);
      setNewName('');
      setNewExpiry('');
      setCreatedKey(response.data);
      loadKeys();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create key');
    } finally {
      setCreating(false);
    }
  };

  const handleDownloadGuide = async () => {
    try {
      const response = await ordersApiKeysAPI.downloadGuide();
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: 'application/pdf' })
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = 'gopak_orders_api_v1.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download the API guide');
    }
  };

  const handleCopyKey = () => {
    navigator.clipboard.writeText(createdKey.key).then(
      () => toast.success('Key copied to clipboard'),
      () => toast.error('Copy failed — select and copy the key manually')
    );
  };

  const handleRevoke = async () => {
    try {
      await ordersApiKeysAPI.revoke(revokeTarget.id);
      toast.success(`Key "${revokeTarget.name}" revoked`);
      setRevokeTarget(null);
      loadKeys();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to revoke key');
    }
  };

  const handleDelete = async () => {
    try {
      await ordersApiKeysAPI.delete(deleteTarget.id);
      toast.success(`Key "${deleteTarget.name}" deleted`);
      setDeleteTarget(null);
      loadKeys();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete key');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Key color="primary" fontSize="large" />
          <Typography variant="h4">Orders API</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdf />}
            onClick={handleDownloadGuide}
          >
            API Guide (PDF)
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
            Create API Key
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Read-only orders feed</Typography>
        <Typography variant="body2" color="text.secondary" component="div">
          External systems (e.g. the Willowbrook Management Hub) sync orders from{' '}
          <code>{'{server}'}/api/v1/orders/</code> using a key created here, sent as{' '}
          <code>Authorization: Api-Key &lt;key&gt;</code> on every request.
          Rate limit: 120 requests/minute per key. The API is read-only — a key
          grants no access to anything else in Gopak.
        </Typography>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Key</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Expires</TableCell>
              <TableCell>Last Used</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {keys.map((key) => {
              const chip = STATUS_CHIP[key.status] || { label: key.status, color: 'default' };
              return (
                <TableRow key={key.id}>
                  <TableCell>{key.name}</TableCell>
                  <TableCell><code>{key.prefix}…</code></TableCell>
                  <TableCell>
                    <Chip size="small" label={chip.label} color={chip.color} />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={`by ${key.created_by || 'unknown'}`}>
                      <span>{formatDate(key.created_at)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{key.expires_at ? formatDate(key.expires_at) : 'Never'}</TableCell>
                  <TableCell>{formatDateTime(key.last_used_at)}</TableCell>
                  <TableCell align="right">
                    {key.status === 'active' && (
                      <Tooltip title="Revoke — the key stops working immediately">
                        <IconButton color="warning" onClick={() => setRevokeTarget(key)}>
                          <Block />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete">
                      <IconButton color="error" onClick={() => setDeleteTarget(key)}>
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
            {!loading && keys.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    No API keys yet — create one to give an external system access
                    to the orders feed.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Usage */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4, mb: 2 }}>
        <Typography variant="h5">Usage</Typography>
        <Button startIcon={<Refresh />} onClick={loadUsage}>Refresh</Button>
      </Box>

      {usage && (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} md={3}>
              <StatCard
                label="Requests (24 h)"
                value={usage.requests_24h}
                hint={
                  usage.per_key_24h.length
                    ? usage.per_key_24h
                        .map((k) => `${k.key_name}: ${k.count}`)
                        .join(' · ')
                    : 'no requests yet'
                }
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard
                label="Errors (24 h)"
                value={usage.errors_24h}
                hint="non-200 responses (auth failures, throttling, bad requests)"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard
                label="Avg response (24 h)"
                value={`${usage.avg_duration_ms_24h} ms`}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard
                label="Requests (7 days)"
                value={usage.requests_7d}
                hint={
                  usage.daily.length
                    ? usage.daily
                        .map((d) => `${d.date.slice(5)}: ${d.count}`)
                        .join(' · ')
                    : undefined
                }
              />
            </Grid>
          </Grid>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Key</TableCell>
                  <TableCell>Request</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Time (ms)</TableCell>
                  <TableCell>From IP</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {usage.recent.map((log, i) => (
                  <TableRow key={i} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {formatDateTime(log.timestamp)}
                    </TableCell>
                    <TableCell>
                      {log.key_name || (
                        <Chip size="small" label="auth failed" color="error" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 380, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <Tooltip title={`${log.path}${log.query ? '?' + log.query : ''}`}>
                        <code style={{ fontSize: '0.78rem' }}>
                          {log.path.replace('/api/v1', '')}
                          {log.query ? `?${log.query}` : ''}
                        </code>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={log.status_code} color={statusColor(log.status_code)} />
                    </TableCell>
                    <TableCell align="right">{log.duration_ms}</TableCell>
                    <TableCell>{log.remote_addr}</TableCell>
                  </TableRow>
                ))}
                {usage.recent.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary" sx={{ py: 2 }}>
                        No requests logged yet — activity will appear here as soon
                        as a consumer calls the API.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Showing the last 50 requests. Logs are kept for {usage.retention_days} days.
          </Typography>
        </>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create API Key</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Name"
            placeholder="e.g. Willowbrook Management Hub"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth
            type="date"
            label="Expires (optional)"
            value={newExpiry}
            onChange={(e) => setNewExpiry(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="Leave empty for a key that never expires"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* One-time key reveal */}
      <Dialog open={!!createdKey} maxWidth="sm" fullWidth>
        <DialogTitle>API Key Created</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Copy this key now — it is shown only once and cannot be recovered,
            only replaced.
          </Alert>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1.5,
              bgcolor: 'action.hover',
              borderRadius: 1,
              fontFamily: 'monospace',
              wordBreak: 'break-all',
            }}
          >
            <span>{createdKey?.key}</span>
            <Tooltip title="Copy to clipboard">
              <IconButton onClick={handleCopyKey}><ContentCopy /></IconButton>
            </Tooltip>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setCreatedKey(null)}>
            I've copied the key
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke confirmation */}
      <Dialog open={!!revokeTarget} onClose={() => setRevokeTarget(null)}>
        <DialogTitle>Revoke API key?</DialogTitle>
        <DialogContent>
          <Typography>
            "{revokeTarget?.name}" ({revokeTarget?.prefix}…) will stop working
            immediately. Any system using it will get 401 errors until it is
            given a new key. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeTarget(null)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleRevoke}>
            Revoke
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete API key?</DialogTitle>
        <DialogContent>
          <Typography>
            Permanently delete "{deleteTarget?.name}" ({deleteTarget?.prefix}…)
            and its usage history?
            {deleteTarget?.status === 'active' && ' The key is currently active.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default OrdersApiKeys;
